package team.platforma.multiplatform_material_extension.calendar.style

import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import materialextension.multiplatform_material_extension.generated.resources.Res
import materialextension.multiplatform_material_extension.generated.resources.mel_cancel
import materialextension.multiplatform_material_extension.generated.resources.mel_clear
import materialextension.multiplatform_material_extension.generated.resources.mel_select
import org.jetbrains.compose.resources.StringResource
import team.platforma.multiplatform_material_extension.ui.dialog.style.DialogStyle


object CalendarViewStyle {
    private var mDateStyle = DateStyle()
    private var mPeriodStyle = PeriodStyle()
    private var mBadgeStyle = BadgeStyle()
    private var mMonthStyle = MonthStyle()
    private var mPikerStyle: PickerStyle = PickerStyle()
    @Composable
    fun define(
        dateStyle: DateStyle = DateStyle(),
        periodStyle: PeriodStyle = PeriodStyle(),
        badgeStyle: BadgeStyle = BadgeStyle(),
        monthStyle: MonthStyle = MonthStyle(),
        pikerStyle: PickerStyle = PickerStyle()
    ) {
        mDateStyle = dateStyle
        mPeriodStyle = periodStyle
        mBadgeStyle = badgeStyle
        mMonthStyle = monthStyle
        mPikerStyle = pikerStyle
    }

    @Composable
    internal fun dateStyle(): GlobalDateStyle {
        return GlobalDateStyle(
            checkedDateBackground = mDateStyle.checkedDateBackground
                ?: MaterialTheme.colorScheme.primary,
            defaultDateBackground = mDateStyle.defaultDateBackground ?: Color.Transparent,
            borderWidth = mDateStyle.borderWidth,
            checkedDateBorder = mDateStyle.checkedDateBorder
                ?: MaterialTheme.colorScheme.onPrimaryContainer,
            defaultDateBorder = mDateStyle.defaultDateBorder ?: Color.Transparent,
            currentDateBorder = mDateStyle.currentDateBorder ?: MaterialTheme.colorScheme.primary,
            enabledDateText = mDateStyle.enabledDateText ?: MaterialTheme.colorScheme.onBackground,
            disableDateText = mDateStyle.disableDateText
                ?: MaterialTheme.colorScheme.outlineVariant,
            checkedDateText = mDateStyle.checkedDateText ?: MaterialTheme.colorScheme.onPrimary,
            shape = mDateStyle.shape,
            fontSize = mDateStyle.fontSize,
            fontFamily = mDateStyle.fontFamily,
            fontWeight = mDateStyle.fontWeight,
            cellPadding = mDateStyle.cellPadding,
            periodDateText = mDateStyle.periodDateText ?: MaterialTheme.colorScheme.tertiary
        )
    }

    @Composable
    internal fun periodStyle(): GlobalPeriodStyle {
        val defaultColor = MaterialTheme.colorScheme.outlineVariant
        return GlobalPeriodStyle(
            startPeriod = GlobalStartPeriod(
                color = mPeriodStyle.startPeriod.color ?: defaultColor,
                shape = mPeriodStyle.startPeriod.shape,
                padding = mPeriodStyle.startPeriod.padding
            ),
            endPeriod = GlobalEndPeriod(
                color = mPeriodStyle.endPeriod.color ?: defaultColor,
                shape = mPeriodStyle.endPeriod.shape,
                padding = mPeriodStyle.endPeriod.padding
            ),
            trackPeriod = GlobalTrackPeriod(
                color = mPeriodStyle.trackPeriod.color ?: defaultColor,
                shape = mPeriodStyle.trackPeriod.shape,
                padding = mPeriodStyle.trackPeriod.padding,
                startShape = mPeriodStyle.trackPeriod.startShape,
                endShape = mPeriodStyle.trackPeriod.endShape,
                startPadding = mPeriodStyle.trackPeriod.startPadding,
                endPadding = mPeriodStyle.trackPeriod.endPadding,
                startColor = mPeriodStyle.trackPeriod.startColor ?: defaultColor,
                endColor = mPeriodStyle.trackPeriod.endColor ?: defaultColor
            ),
        )
    }

    @Composable
    internal fun badgeStyle(): GlobalBadgeStyle {
        return GlobalBadgeStyle(
            shape = mBadgeStyle.shape,
            background = mBadgeStyle.background ?: MaterialTheme.colorScheme.primaryContainer,
            textColor = mBadgeStyle.textColor ?: MaterialTheme.colorScheme.onPrimaryContainer,
            fontWeight = mBadgeStyle.fontWeight,
            fontSize = mBadgeStyle.fontSize
        )
    }

    @Composable
    internal fun monthStyle(): GlobalMonthStyle {
        return GlobalMonthStyle(
            color = mMonthStyle.color ?: MaterialTheme.colorScheme.onBackground
        )
    }

    @Composable
    internal fun pikerStyle(): GlobalPickerStyle {
       return GlobalPickerStyle(
           dialogStyle = mPikerStyle.dialogStyle,
           positiveButton = GlobalPikerButton(
               color = mPikerStyle.positiveButton.color ?: MaterialTheme.colorScheme.primary,
               style = mPikerStyle.positiveButton.style ?: MaterialTheme.typography.bodyMedium,
               label = mPikerStyle.positiveButton.label ?: Res.string.mel_select
           ),
           negativeButton = GlobalPikerButton(
               color = mPikerStyle.negativeButton.color ?: MaterialTheme.colorScheme.primary,
               style = mPikerStyle.negativeButton.style ?: MaterialTheme.typography.bodyMedium,
               label = mPikerStyle.negativeButton.label ?: Res.string.mel_cancel
           ),
           disableButton = GlobalPikerButton(
               color = mPikerStyle.disableButton.color ?: MaterialTheme.colorScheme.outlineVariant,
               style = mPikerStyle.disableButton.style ?: MaterialTheme.typography.bodyMedium,
               label = Res.string.mel_select
           ),
           neutralButton = GlobalPikerButton(
               color = mPikerStyle.neutralButton.color ?: MaterialTheme.colorScheme.primary,
               style = mPikerStyle.neutralButton.style ?: MaterialTheme.typography.bodyMedium,
               label = mPikerStyle.neutralButton.label ?: Res.string.mel_clear
           )
       )
    }
}

data class DateStyle(
    var checkedDateBackground: Color? = null,
    var defaultDateBackground: Color? = null,
    var borderWidth: Dp = 2.dp,
    var checkedDateBorder: Color? = null,
    var defaultDateBorder: Color? = null,
    var currentDateBorder: Color? = null,
    var enabledDateText: Color? = null,
    var disableDateText: Color? = null,
    var checkedDateText: Color? = null,
    var periodDateText: Color? = null,
    var shape: Shape = RoundedCornerShape(12.dp),
    var fontSize: Float = .35F,
    var fontFamily: FontFamily = FontFamily.Default,
    var fontWeight: FontWeight = FontWeight.Normal,
    var cellPadding: CellPadding = CellPadding()
)

internal data class GlobalDateStyle(
    val checkedDateBackground: Color,
    val defaultDateBackground: Color,
    val borderWidth: Dp,
    val checkedDateBorder: Color,
    val defaultDateBorder: Color,
    val currentDateBorder: Color,
    val enabledDateText: Color,
    val disableDateText: Color,
    val checkedDateText: Color,
    val shape: Shape,
    val fontSize: Float,
    val fontFamily: FontFamily,
    val fontWeight: FontWeight,
    val cellPadding: CellPadding,
    var periodDateText: Color,
)

data class CellPadding(
    var horizontal: Dp = 4.dp,
    var vertical: Dp = 4.dp
)

data class PeriodStyle(
    var startPeriod: StartPeriod = StartPeriod(),
    var endPeriod: EndPeriod = EndPeriod(),
    var trackPeriod: TrackPeriod = TrackPeriod(),
)

data class StartPeriod(
    var color: Color? = null,
    var shape: Shape = RoundedCornerShape(topStart = 12.dp, bottomStart = 12.dp),
    var padding: PeriodPadding = PeriodPadding(start = 4.dp)
)

data class EndPeriod(
    var color: Color? = null,
    var shape: Shape = RoundedCornerShape(topEnd = 12.dp, bottomEnd = 12.dp),
    var padding: PeriodPadding = PeriodPadding(end = 4.dp)
)

data class TrackPeriod(
    var color: Color? = null,
    var shape: Shape = RoundedCornerShape(0),
    var padding: PeriodPadding = PeriodPadding(),
    var startShape: Shape = shape,
    var endShape: Shape = shape,
    var startPadding: PeriodPadding = padding,
    var endPadding: PeriodPadding = padding,
    var startColor: Color? = color,
    var endColor: Color? = color
)

data class PeriodPadding(
    var top: Dp = 4.dp,
    var bottom: Dp = 4.dp,
    var start: Dp = 0.dp,
    var end: Dp = 0.dp
)

internal data class GlobalPeriodStyle(
    val startPeriod: GlobalStartPeriod,
    val endPeriod: GlobalEndPeriod,
    val trackPeriod: GlobalTrackPeriod,
)

internal data class GlobalStartPeriod(
    val color: Color,
    val shape: Shape,
    val padding: PeriodPadding
)

data class GlobalEndPeriod(
    val color: Color,
    val shape: Shape,
    val padding: PeriodPadding
)

data class GlobalTrackPeriod(
    val color: Color,
    val shape: Shape,
    val padding: PeriodPadding,
    val startShape: Shape,
    val endShape: Shape,
    val startPadding: PeriodPadding,
    val endPadding: PeriodPadding,
    var startColor: Color,
    var endColor: Color
)

data class BadgeStyle(
    var shape: Shape = RoundedCornerShape(100),
    var background: Color? = null,
    var textColor: Color? = null,
    var fontWeight: FontWeight = FontWeight.SemiBold,
    var fontSize: Float = .65F
)

internal data class GlobalBadgeStyle(
    val shape: Shape,
    val background: Color,
    val textColor: Color,
    val fontWeight: FontWeight,
    val fontSize: Float
)

data class MonthStyle(
    var color: Color? = null
)

internal data class GlobalMonthStyle(
    val color: Color
)

data class PickerStyle(
    var dialogStyle: DialogStyle? = null,
    var button: PikerButton = PikerButton(),
    var positiveButton: PikerButton = button,
    var negativeButton: PikerButton = button,
    var disableButton: PikerButton = button,
    var neutralButton: PikerButton = button
)

data class PikerButton(
    var color: Color? = null,
    var style: TextStyle? = null,
    var label: StringResource? = null
)

internal data class GlobalPickerStyle(
    val dialogStyle: DialogStyle?,
    val positiveButton: GlobalPikerButton,
    val negativeButton: GlobalPikerButton,
    val disableButton: GlobalPikerButton,
    val neutralButton: GlobalPikerButton
)

internal data class GlobalPikerButton(
    val color: Color,
    val style: TextStyle,
    val label: StringResource
)
